import { PAGE_SIZES, PERIOD_DAYS } from '@dbrain/shared';
import { Chip, Icons, Menu, MenuItem, ScrollArea } from '@dbrain/ui';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

/**
 * The management lists' one table (Phase 13d-2, 06 §23.3): a real table
 * with a fixed layout and one <colgroup>, so the header and the cells share
 * one column grid. Numbers sit right under right-aligned headers with
 * tabular figures; a sortable header is a button with aria-sort. Widths
 * are classes (the CSP allows no inline styles). The row's name column has
 * no width and takes the rest; a frame narrower than the fixed columns plus
 * its least width scrolls sideways.
 */
export type ColumnWidth = 'check' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'menu' | 'actions' | 'auto';

/** the pixel widths of the `dt-w-*` classes in admin.css; `auto` columns get at least AUTO_MIN */
const WIDTHS: Record<Exclude<ColumnWidth, 'auto'>, number> = { check: 44, menu: 56, xs: 84, sm: 116, md: 152, lg: 220, xl: 300, actions: 248 };
const AUTO_MIN = 140;

function minWidth(widths: ColumnWidth[]): number {
  return widths.reduce((sum, width) => sum + (width === 'auto' ? AUTO_MIN : WIDTHS[width]), 0);
}

/** The least width a table of these columns needs before it scrolls sideways (its `--dt-min-width`). */
export function tableMinWidth(columns: readonly Pick<Column<unknown>, 'width'>[], extras: { selection?: boolean; rowMenu?: boolean } = {}): number {
  return minWidth([...(extras.selection ? ['check' as const] : []), ...columns.map((c) => c.width ?? 'auto'), ...(extras.rowMenu ? ['menu' as const] : [])]);
}

export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** a number: right-aligned, tabular figures */
  numeric?: boolean;
  width?: ColumnWidth;
  /** the server's sort key; makes the header a button */
  sort?: string;
  /** the total row's cell */
  total?: ReactNode;
  className?: string;
}

export interface Selection<T> {
  selected: ReadonlySet<string>;
  onToggle: (key: string, on: boolean) => void;
  onTogglePage: (on: boolean) => void;
  label: (row: T) => string;
}

export interface DataTableProps<T> {
  /** the table's accessible name */
  label: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  sort?: { key: string; desc: boolean };
  onSort?: (key: string) => void;
  selection?: Selection<T>;
  /** a "…" menu at the end of each row */
  rowMenu?: (row: T) => ReactNode;
  rowClassName?: (row: T) => string | undefined;
  rowTestId?: (row: T) => string;
  /** more data-* attributes of a row (a document's name and state for the tests) */
  rowData?: (row: T) => Record<`data-${string}`, string>;
  /** a header row before every new group (the audit log's days) */
  groupOf?: (row: T) => string;
  groupLabel?: (group: string) => ReactNode;
  /** the total row's first cell (the others from the columns' `total`) */
  totalLabel?: ReactNode;
  empty?: ReactNode;
  loading?: boolean;
  footer?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  sort,
  onSort,
  selection,
  rowMenu,
  rowClassName,
  rowTestId,
  rowData,
  groupOf,
  groupLabel,
  totalLabel,
  empty,
  loading,
  footer,
  className,
  'data-testid': testId,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const span = columns.length + (selection ? 1 : 0) + (rowMenu ? 1 : 0);
  const pageKeys = rows.map(rowKey);
  const allSelected = selection ? pageKeys.length > 0 && pageKeys.every((key) => selection.selected.has(key)) : false;
  const someSelected = selection ? pageKeys.some((key) => selection.selected.has(key)) : false;
  const headerBox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (headerBox.current) headerBox.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);
  const frame = useRef<HTMLDivElement>(null);
  const table = useRef<HTMLTableElement>(null);
  const least = minWidth([...(selection ? ['check' as const] : []), ...columns.map((c) => c.width ?? 'auto'), ...(rowMenu ? ['menu' as const] : [])]);
  useLayoutEffect(() => {
    table.current?.style.setProperty('--dt-min-width', `${least}px`);
  }, [least]);
  let lastGroup: string | null = null;
  return (
    <div ref={frame} className={['dt-frame', className ?? ''].join(' ').trim()} data-testid={testId} aria-busy={loading || undefined}>
      <ScrollArea label={label} className="dt-scroll">
        <table ref={table} className="dt" aria-label={label}>
          <colgroup>
            {selection ? <col className="dt-w-check" /> : null}
            {columns.map((column) => (
              <col key={column.id} className={`dt-w-${column.width ?? 'auto'}`} />
            ))}
            {rowMenu ? <col className="dt-w-menu" /> : null}
          </colgroup>
          <thead>
            <tr>
              {selection ? (
                <th scope="col" className="dt__check">
                  <input
                    ref={headerBox}
                    type="checkbox"
                    checked={allSelected}
                    aria-label={t('admin.table.selectPage')}
                    onChange={(e) => selection.onTogglePage(e.target.checked)}
                    data-testid="dt-select-page"
                  />
                </th>
              ) : null}
              {columns.map((column) => {
                const sorted = column.sort !== undefined && sort?.key === column.sort;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    className={[column.numeric ? 'dt__num' : '', column.className ?? ''].join(' ').trim() || undefined}
                    aria-sort={sorted ? (sort?.desc ? 'descending' : 'ascending') : undefined}
                    data-column={column.id}
                  >
                    {column.sort !== undefined && onSort ? (
                      <button type="button" className={`dt__sort${sorted ? ' dt__sort--active' : ''}`} onClick={() => onSort(column.sort as string)} data-testid={`dt-sort-${column.id}`}>
                        <span>{column.header}</span>
                        {sorted ? sort?.desc ? <Icons.chevronDown size={14} /> : <Icons.chevronUp size={14} /> : <Icons.chevronDown size={14} className="dt__sort-hint" />}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {rowMenu ? (
                <th scope="col" className="dt__menu">
                  <span className="ui-visually-hidden">{t('admin.table.actions')}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {totalLabel !== undefined && rows.length > 0 ? (
              <tr className="dt__total" data-testid="dt-total">
                {selection ? <td /> : null}
                {columns.map((column, i) => (
                  <td key={column.id} className={column.numeric ? 'dt__num' : undefined} data-column={column.id}>
                    {i === 0 ? totalLabel : column.total ?? null}
                  </td>
                ))}
                {rowMenu ? <td /> : null}
              </tr>
            ) : null}
            {rows.map((row) => {
              const key = rowKey(row);
              const group = groupOf?.(row) ?? null;
              const header =
                group !== null && group !== lastGroup ? (
                  <tr key={`group-${group}`} className="dt__group">
                    <th colSpan={span} scope="colgroup">
                      {groupLabel ? groupLabel(group) : group}
                    </th>
                  </tr>
                ) : null;
              lastGroup = group;
              const selected = selection?.selected.has(key) ?? false;
              return [
                header,
                <tr
                  key={key}
                  className={[selected ? 'dt__row--selected' : '', rowClassName?.(row) ?? ''].join(' ').trim() || undefined}
                  data-testid={rowTestId?.(row)}
                  {...rowData?.(row)}
                >
                  {selection ? (
                    <td className="dt__check">
                      <input type="checkbox" checked={selected} aria-label={selection.label(row)} onChange={(e) => selection.onToggle(key, e.target.checked)} data-testid="dt-select" />
                    </td>
                  ) : null}
                  {columns.map((column) => (
                    <td key={column.id} className={[column.numeric ? 'dt__num' : '', column.className ?? ''].join(' ').trim() || undefined} data-column={column.id}>
                      {column.cell(row)}
                    </td>
                  ))}
                  {rowMenu ? <td className="dt__menu">{rowMenu(row)}</td> : null}
                </tr>,
              ];
            })}
          </tbody>
        </table>
        {rows.length === 0 && !loading ? <div className="dt__empty">{empty ?? t('admin.table.empty')}</div> : null}
      </ScrollArea>
      {footer}
    </div>
  );
}

/** "1–25 / 286 aktif üye", the page size and the page buttons (1 2 3 … 12). */
export function Pagination({ page, size, total, onPage, onSize, noun, note }: { page: number; size: number; total: number; onPage: (page: number) => void; onSize: (size: number) => void; noun: string; note?: string }) {
  const { t, i18n } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / size));
  const from = total === 0 ? 0 : (page - 1) * size + 1;
  const to = Math.min(total, page * size);
  const numbers = pageNumbers(page, pages);
  return (
    <div className="dt-footer" data-testid="dt-footer">
      <span className="dt-footer__count" data-testid="dt-count">
        {t('admin.table.range', { from: from.toLocaleString(i18n.language), to: to.toLocaleString(i18n.language), total: total.toLocaleString(i18n.language), noun })}
        {note && total > 0 ? <span className="muted"> · {note}</span> : null}
      </span>
      <span className="dt-footer__pages">
        <label className="dt-footer__size">
          <span>{t('admin.table.perPage')}</span>
          <select value={size} onChange={(e) => onSize(Number(e.target.value))} data-testid="dt-size">
            {PAGE_SIZES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </label>
        <nav className="dt-pages" aria-label={t('admin.table.pages')}>
          <button type="button" className="dt-pages__step" disabled={page <= 1} aria-label={t('admin.table.previous')} onClick={() => onPage(page - 1)}>
            <Icons.chevronLeft size={16} />
          </button>
          {numbers.map((value, i) =>
            value === null ? (
              <span key={`gap-${i}`} className="dt-pages__gap" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={value}
                type="button"
                className={`dt-pages__page${value === page ? ' dt-pages__page--current' : ''}`}
                aria-current={value === page ? 'page' : undefined}
                onClick={() => onPage(value)}
                data-testid={`dt-page-${value}`}
              >
                {value}
              </button>
            ),
          )}
          <button type="button" className="dt-pages__step" disabled={page >= pages} aria-label={t('admin.table.next')} onClick={() => onPage(page + 1)} data-testid="dt-next">
            <Icons.chevronRight size={16} />
          </button>
        </nav>
      </span>
    </div>
  );
}

/** 1 2 3 … 12: the first, the last, and the pages around the current one. */
export function pageNumbers(page: number, pages: number): (number | null)[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const around = new Set([1, pages, page - 1, page, page + 1, ...(page <= 3 ? [2, 3] : []), ...(page >= pages - 2 ? [pages - 1, pages - 2] : [])]);
  const sorted = [...around].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out: (number | null)[] = [];
  for (const n of sorted) {
    const last = out.at(-1);
    if (typeof last === 'number' && n - last > 1) out.push(null);
    out.push(n);
  }
  return out;
}

/**
 * A list's state in the address (D-100): search, filters, sort, page, size and period survive a reload and
 * make "Üyelerde göster" a link. Changing anything but the page goes back to the first page.
 */
export function useListParams<K extends string>(defaults: Partial<Record<K | 'page' | 'size' | 'sort' | 'dir' | 'q' | 'days', string>> = {}) {
  const [params, setParams] = useSearchParams();
  const get = (key: K | 'page' | 'size' | 'sort' | 'dir' | 'q' | 'days'): string | undefined => params.get(key) ?? (defaults as Record<string, string | undefined>)[key] ?? undefined;
  const set = (changes: Partial<Record<string, string | null | undefined>>) => {
    const next = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, value);
    }
    if (!('page' in changes)) next.delete('page');
    setParams(next, { replace: true });
  };
  const page = Number(get('page') ?? 1) || 1;
  const size = Number(get('size') ?? 25) || 25;
  /** the query string for the API: every set parameter (the defaults are the server's) */
  const query = (extra: Record<string, string | undefined> = {}) => {
    const out = new URLSearchParams();
    for (const [key, value] of params) out.set(key, value);
    for (const [key, value] of Object.entries(defaults as Record<string, string | undefined>)) if (value !== undefined && !out.has(key)) out.set(key, value);
    for (const [key, value] of Object.entries(extra)) if (value !== undefined) out.set(key, value);
    return out.toString();
  };
  return { get, set, page, size, query, params };
}

/** The sort of a list: a click on the current column turns it around, a new column starts with its own direction. */
export function sortState(list: { get: (key: 'sort' | 'dir') => string | undefined; set: (changes: Record<string, string | null>) => void }, fallback: string, descByDefault: (key: string) => boolean) {
  const key = list.get('sort') ?? fallback;
  const dir = list.get('dir');
  const desc = dir ? dir === 'desc' : descByDefault(key);
  return {
    sort: { key, desc },
    onSort: (next: string) => {
      if (next === key) list.set({ sort: next, dir: desc ? 'asc' : 'desc' });
      else list.set({ sort: next, dir: null });
    },
  };
}

/** The search box: it waits until typing pauses before it asks the server. */
export function SearchBox({ value, onChange, placeholder, testId }: { value: string; onChange: (value: string) => void; placeholder: string; testId?: string }) {
  const [text, setText] = useState(value);
  const changed = useRef(onChange);
  changed.current = onChange;
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const timer = setTimeout(() => changed.current(text), 300);
    return () => clearTimeout(timer);
  }, [text, value]);
  return (
    <label className="dt-search">
      <Icons.search size={16} aria-hidden="true" />
      <input type="search" value={text} placeholder={placeholder} aria-label={placeholder} onChange={(e) => setText(e.target.value)} data-testid={testId} />
    </label>
  );
}

export interface FilterOption {
  value: string;
  label: ReactNode;
}

/** "Domain: Tümü (6) ⌄": a filter as a small menu. */
export function FilterMenu({ label, value, options, allLabel, onChange, testId }: { label: string; value: string | undefined; options: FilterOption[]; allLabel: ReactNode; onChange: (value: string | null) => void; testId?: string }) {
  const current = options.find((o) => o.value === value);
  return (
    <Menu
      trigger={
        <>
          <span>
            {label}: {current ? current.label : allLabel}
          </span>
          <Icons.chevronDown size={14} />
        </>
      }
      triggerClassName="dt-filter"
      panelClassName="dt-filter__panel"
      data-testid={testId}
    >
      <MenuItem aria-pressed={!current} onClick={() => onChange(null)}>
        {allLabel}
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} aria-pressed={option.value === value} onClick={() => onChange(option.value)} data-testid={testId ? `${testId}-${option.value}` : undefined}>
          {option.label}
        </MenuItem>
      ))}
    </Menu>
  );
}

/** Mutually exclusive filter chips with their counts; pressing the pressed one clears it. */
export function FilterChips<V extends string>({ chips, value, onChange, label }: { chips: { value: V; label: string; count?: number; tone?: 'danger' | 'warning' | 'neutral' }[]; value: V | undefined; onChange: (value: V | null) => void; label: string }) {
  const { i18n } = useTranslation();
  return (
    <div className="dt-chips" role="group" aria-label={label}>
      {chips.map((chip) => (
        <Chip key={chip.value} active={value === chip.value} onClick={() => onChange(value === chip.value ? null : chip.value)} data-testid={`chip-${chip.value}`}>
          {chip.tone && chip.tone !== 'neutral' ? <span className={`ui-dot ui-dot--${chip.tone}`} aria-hidden="true" /> : null}
          {chip.label}
          {chip.count !== undefined ? <span className="dt-chips__count">{chip.count.toLocaleString(i18n.language)}</span> : null}
        </Chip>
      ))}
    </div>
  );
}

/** "Dönem: Son 30 gün ⌄": the period as a small pill next to the title. */
export function PeriodPill({ days, onChange }: { days: number; onChange: (days: number) => void }) {
  const { t } = useTranslation();
  return (
    <Menu
      trigger={
        <>
          <span>{t('admin.period.label', { period: t(`admin.period.d${days}`) })}</span>
          <Icons.chevronDown size={14} />
        </>
      }
      triggerClassName="period-pill"
      align="right"
      data-testid="period-pill"
    >
      {PERIOD_DAYS.map((value) => (
        <MenuItem key={value} aria-pressed={value === days} onClick={() => onChange(value)} data-testid={`period-${value}`}>
          {t(`admin.period.d${value}`)}
        </MenuItem>
      ))}
    </Menu>
  );
}

/** "CSV": the same list, filtered, as a file. */
export function CsvLink({ href, label, testId }: { href: string; label: string; testId?: string }) {
  return (
    <a className="ui-button ui-button--secondary ui-button--sm dt-csv" href={href} download data-testid={testId}>
      <Icons.download size={16} />
      {label}
    </a>
  );
}

/** A count in a sub-tab or a chip ("Üyeler 300"). */
export function TabCount({ value }: { value: number | undefined }) {
  const { i18n } = useTranslation();
  return value === undefined ? null : <span className="sub-tabs__count">{value.toLocaleString(i18n.language)}</span>;
}
